import {
  CsvExportParams,
  GridApi,
  ProcessCellForExportParams
} from "ag-grid-enterprise";
import { ProjectSettingType } from "context/ProjectProvider";
import { ArrayStringCellRenderer } from "entity-app/shared-components/log-render-components/cell-renders";

const getFormattedValues = (params: ProcessCellForExportParams<any>) => {
  const { value, column, formatValue } = params;
  const colDef = column.getColDef();

  const formattedValue = formatValue(value);

  switch (true) {
    case colDef.colId === "linked_submittals":
    case colDef.colId === "linked_materials":
    case colDef.colId === "linked_tasks":
    case colDef.colId === "locations":
    case colDef.cellRenderer === ArrayStringCellRenderer:
      {
        const arr = (value || []) as Array<{ id: string; name: string }>;
        const formattedItems = arr.map((item) => item.name);
        return formattedItems.join("\n");
      }
      break;
    default:
      return (formattedValue || value) ?? "";
  }
};

type TSortPosition = {
  colId: string;
  dependentColId: string;
  position: "before" | "after";
};

const getParams = (props: {
  entityName: string;
  projectDetails: ProjectSettingType;
  api: GridApi;
  includedColumns: Array<string>;
  excludedColumns: Array<string>;
  sortPosition?: Array<TSortPosition>;
}): CsvExportParams => {
  const {
    entityName,
    projectDetails,
    api,
    includedColumns,
    excludedColumns,
    sortPosition = []
  } = props;
  const columnKeys = api
    ?.getAllGridColumns()
    ?.filter((col) => {
      const colId = col.getColId();
      if (includedColumns.includes(colId)) return true;

      if (excludedColumns.includes(colId)) return false;

      return col.isVisible();
    })
    .map((col) => col.getColId());

  sortPosition.forEach((sort) => {
    const dependentColumnIndex = columnKeys.indexOf(sort.dependentColId);
    // If dependent column is found, we just insert after the current column
    if (dependentColumnIndex > -1) {
      columnKeys.splice(dependentColumnIndex + 1, 0, sort.colId);
    }
  });

  return {
    columnKeys,
    processCellCallback(params): string {
      return getFormattedValues(params);
    },
    fileName: `${entityName}_${projectDetails.name}.csv`
  };
};

export const exportEntityLogAsCSV = (props: {
  entityName: string;
  projectDetails: ProjectSettingType;
  gridRef: {
    api: GridApi<any>;
  };
  includedColumns: Array<string>;
  excludedColumns: Array<string>;
  sortPosition?: Array<TSortPosition>;
}) => {
  const {
    gridRef,
    projectDetails,
    entityName,
    includedColumns,
    excludedColumns,
    sortPosition
  } = props;
  const { api } = gridRef;
  api.exportDataAsCsv(
    getParams({
      entityName,
      projectDetails,
      api,
      includedColumns,
      excludedColumns,
      sortPosition
    })
  );
};
